import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { isEqual, partition, sortBy, uniqBy, uniqWith } from 'lodash-es';
import moment from 'moment';
import type { Delta } from 'quill/core';
import Quill from 'quill/core';
import React, { useLayoutEffect, useMemo, useState } from 'react';
import type { ActivityItemIconColor } from './ActivityItem';
import ActivityItem, { ActivitySeparator } from './ActivityItem';
import * as styles from './ControlBarActivity.module.scss';
import sumHistoryStats from './history/sumHistoryStats';
import { useAppDispatch } from '../app/redux/useAppSelector';
import { EditorActions } from '../editor/editorSlice';
import { gql } from '../graphql/generated';
import CommentPreview from './comment/CommentPreview';
import Stats from './Stats';
import useCanDisplayNewPostHistoryView from './useCanDisplayNewPostHistoryView';
import { useQuill } from '../editor/QuillContext';
import collectThreadAndAnnotationIds from './utils/collectThreadAndAnnotationIds';
import InfiniteScrollWaypoint from '../components/InfiniteScrollWaypoint';
import LoadingIndicator from '../components/LoadingIndicator';
import type { HistoryGroup } from './history/partitionAndGroupHistories';
import partitionAndGroupHistories from './history/partitionAndGroupHistories';
import isInlineTextInsertion from '../editor/utils/isInlineTextInsertion';
import type { DocumentType } from '../graphql/generated/gql';
import { AlphaPostActivityType } from '../graphql/generated/graphql';
import archiveIcon from '../icons/non-prefilled/16x16/archive.svg';
import commentIcon from '../icons/non-prefilled/16x16/comment.svg';
import draftsIcon from '../icons/non-prefilled/16x16/drafts.svg';
import editIcon from '../icons/non-prefilled/16x16/edit.svg';
import helpIcon from '../icons/non-prefilled/16x16/help.svg';
import publishIcon from '../icons/non-prefilled/16x16/publish.svg';
import replyIcon from '../icons/non-prefilled/16x16/reply.svg';
import unarchiveIcon from '../icons/non-prefilled/16x16/unarchive.svg';
import unpublishIcon from '../icons/non-prefilled/16x16/unpublish.svg';
import verifiedIcon from '../icons/non-prefilled/16x16/verifiedFilled.svg';
import { useGetUser } from '../user/useAllUsers';
import exhaustiveMatchGuard from '../utils/exhaustiveMatchGuard';
import { isFeatureEnabled } from '../utils/features';
import { isCommentOrPublicCommentThread } from '../utils/post/threadUtils';
import historyPath from '../utils/url/historyPath';

const MAX_COMMENT_LINE_COUNT = 3;

// TODO: Before releasing the feature, we will turn this query into a fragment and include it in the PostView query
const CONTROL_BAR_ACTIVITY_QUERY = gql(/** @complexity 25 */ `
  query ControlBarActivity($postId: ID!, $beforeAt: Datetime!) {
    alphaPostActivities(postId: $postId, beforeAt: $beforeAt) {
      actedAt
      action
      actors {
        id
        ...useGetUser_user
      }
    }
    post(id: $postId) {
      id
      threads {
        id
        ... on CommentThread {
          id
          comments {
            id
            insertedAt
            content
            author {
              id
              ...useGetUser_user
            }
          }
        }
      }
      histories {
        id
        ...partitionAndGroupHistories_history
        ...sumHistoryStats_history
        authors {
          id
          ...useGetUser_user
        }
      }
      ...historyPath_post
    }
  }
`);

interface ControlBarActivityProps {
  postId: string;
  closePopover: () => void;
  fullHeight?: boolean;
}

const ControlBarActivity = ({
  postId,
  closePopover,
  fullHeight,
}: ControlBarActivityProps) => {
  // Do initial fetch with a fixed time to enable caching
  const END_OF_TIME = '2099-12-31T00:00:00.0Z';

  const { data, loading, fetchMore } = useQuery(CONTROL_BAR_ACTIVITY_QUERY, {
    variables: {
      postId,
      beforeAt: END_OF_TIME,
    },
  });

  // TODO: Add error handling if we decide not to move this to the PostView query
  if (loading || !data) {
    return (
      <div className={styles.loader}>
        <LoadingIndicator delay={0} />
      </div>
    );
  }

  const fetchMoreActivities = async () => {
    const lastActivity = data.alphaPostActivities?.at(-1);
    if (!lastActivity) {
      return;
    }

    // There might be two activities with the same timestamp on the edge of two pages.
    // To ensure we don't skip any of them, we slightly over-fetch to include
    // the last activity from the previous page on the next page.
    const beforeAt = moment(lastActivity.actedAt).add(1, 'ms').toISOString();

    await fetchMore({
      variables: {
        postId,
        beforeAt,
      },
      updateQuery(previousResult, { fetchMoreResult }) {
        return {
          ...previousResult,
          ...fetchMoreResult,
          alphaPostActivities: uniqWith(
            [
              ...previousResult.alphaPostActivities,
              ...fetchMoreResult.alphaPostActivities,
            ],
            isEqual,
          ),
        };
      },
    });
  };

  return (
    <div
      className={classNames(styles.container, {
        [styles.fullHeight]: fullHeight,
      })}
    >
      <UnresolvedActivitiesSection
        threads={data.post.threads}
        onSelectThread={closePopover}
      />
      <AllActivitiesSection
        post={data.post}
        activities={data.alphaPostActivities}
        fetchMoreActivities={fetchMoreActivities}
      />
    </div>
  );
};

interface UnresolvedActivitiesSectionProps {
  threads?: DocumentType<typeof CONTROL_BAR_ACTIVITY_QUERY>['post']['threads'];
  onSelectThread: () => void;
}

const UnresolvedActivitiesSection = ({
  threads,
  onSelectThread,
}: UnresolvedActivitiesSectionProps) => {
  const { quill } = useQuill();
  const dispatch = useAppDispatch();
  const [postThreadsIds, setPostThreadsIds] = useState(new Set<string>());
  const getUser = useGetUser();

  useLayoutEffect(() => {
    if (!quill) return undefined;

    const updatePostThreadsIds = () => {
      const result = collectThreadAndAnnotationIds(quill.getContents().ops);
      setPostThreadsIds((current) =>
        isEqual(current, result.allThreadIds) ? current : result.allThreadIds,
      );
    };

    updatePostThreadsIds();

    const onTextChange = (delta: Delta) => {
      if (isInlineTextInsertion(delta)) return;
      updatePostThreadsIds();
    };

    quill.on(Quill.events.TEXT_CHANGE, onTextChange);

    return () => {
      quill.off(Quill.events.TEXT_CHANGE, onTextChange);
    };
  }, [quill]);

  const visibleCommentThreads = threads
    ?.map((thread) => {
      const lastComment =
        isCommentOrPublicCommentThread(thread) &&
        postThreadsIds.has(thread.id) &&
        thread.comments.at(-1);

      if (!lastComment) {
        return null;
      }

      return {
        id: thread.id,
        lastComment,
        hasReplies: thread.comments.length > 1,
        timestamp: new Date(lastComment.insertedAt).getTime(),
      };
    })
    .filter((item) => item != null)
    .toSorted((a, b) => b.timestamp - a.timestamp);

  if (!visibleCommentThreads?.length) {
    return null;
  }

  const focusCommentThread = (threadId: string) => {
    dispatch(
      EditorActions.focusMagneticWall({
        id: threadId,
        type: 'comment',
        isActive: true,
        hasExpandIntent: false,
      }),
    );
    onSelectThread();
  };

  return (
    <>
      <h3 className={styles.title}>Unresolved</h3>
      <div>
        {visibleCommentThreads.map((thread) => {
          return (
            <ActivityItem
              key={thread.id}
              icon={thread.hasReplies ? replyIcon : commentIcon}
              iconColor="yellow"
              description={
                <CommentPreview
                  collapseLines
                  content={thread.lastComment.content}
                  link={false}
                  maxLines={MAX_COMMENT_LINE_COUNT}
                />
              }
              timestamp={thread.timestamp}
              actors={[getUser(thread.lastComment.author)]}
              onClick={() => focusCommentThread(thread.id)}
              onMouseEnter={() =>
                dispatch(EditorActions.highlightComment(thread.id))
              }
              onMouseLeave={() =>
                dispatch(EditorActions.removeMagneticWallHighlight())
              }
            />
          );
        })}
      </div>
    </>
  );
};

interface AllActivitiesSectionProps {
  post: DocumentType<typeof CONTROL_BAR_ACTIVITY_QUERY>['post'];
  activities: DocumentType<
    typeof CONTROL_BAR_ACTIVITY_QUERY
  >['alphaPostActivities'];
  fetchMoreActivities: () => void;
}

const AllActivitiesSection = ({
  activities,
  post,
  fetchMoreActivities,
}: AllActivitiesSectionProps) => {
  const initialRenderTimestamp = useMemo(() => Date.now(), []);
  const [isForceShowingOldActivities, setIsForceShowingOldActivities] =
    useState(false);
  const canDisplayNewPostHistoryView = useCanDisplayNewPostHistoryView();
  const getUser = useGetUser();

  const recentActivitiesThreshold = moment(initialRenderTimestamp).subtract(
    7,
    'days',
  );

  const lastFetchedActivity = activities.at(-1);

  type ActivityItemType = {
    timestamp: number;
    key: string;
    view: React.ReactNode;
  };

  const postActivities = activities
    .filter((item) => item.action !== AlphaPostActivityType.Edit)
    .map(
      (item): ActivityItemType => ({
        key: `${item.actedAt}-${item.action}`,
        timestamp: new Date(item.actedAt).getTime(),
        view: (
          <ActivityItem
            actors={item.actors.map(getUser)}
            timestamp={item.actedAt}
            {...getDescriptionAndIcon(item)}
          />
        ),
      }),
    );

  const { accessibleHistories, restrictedHistories } =
    partitionAndGroupHistories(post.histories);

  const historyActivities = [...accessibleHistories, ...restrictedHistories]
    .filter(
      (item) =>
        new Date(lastFetchedActivity?.actedAt) <= new Date(item.insertedAt),
    )
    .map((item): ActivityItemType => {
      const stats = sumHistoryStats(item.histories);
      const hasStats = Object.values(stats).some((stat) => stat > 0);
      const actors = uniqBy(
        item.histories.flatMap((history) => history.authors.map(getUser)),
        (user) => user.id,
      );
      return {
        key: `${item.insertedAt}-history}`,
        timestamp: new Date(item.insertedAt).getTime(),
        view: (
          <ActivityItem
            actors={actors}
            icon={editIcon}
            iconColor="pink"
            timestamp={item.insertedAt}
            description={
              hasStats ? (
                <span className={styles.editDescription}>
                  <Stats stats={sumHistoryStats(item.histories)} />
                </span>
              ) : (
                'Edited this post'
              )
            }
            href={getLinkToHistory(post, item, !!canDisplayNewPostHistoryView)}
          />
        ),
      };
    });

  const allActivitiesSorted = sortBy(
    [...postActivities, ...historyActivities],
    (item) => -item.timestamp,
  );

  const [recentActivities, oldActivities] = partition(
    allActivitiesSorted,
    (item) => recentActivitiesThreshold.isBefore(item.timestamp),
  );

  const isShowingOldActivities =
    isForceShowingOldActivities ||
    (recentActivities.length === 0 && oldActivities.length > 0);

  const hasMoreActivitiesToFetch =
    lastFetchedActivity &&
    lastFetchedActivity.action !== AlphaPostActivityType.Create;

  const hasMoreRecentActivitiesToFetch =
    hasMoreActivitiesToFetch && oldActivities.length === 0;

  const hasMoreOldActivitiesToFetch =
    hasMoreActivitiesToFetch && isShowingOldActivities;

  return (
    <>
      <h3 className={styles.title}>All activities</h3>
      <div>
        {recentActivities?.map((activity) => (
          <React.Fragment key={activity.key}>{activity.view}</React.Fragment>
        ))}
        {oldActivities.length > 0 &&
          (isShowingOldActivities ? (
            <>
              {isForceShowingOldActivities && (
                <ActivitySeparator>
                  Showing older than a week ago
                </ActivitySeparator>
              )}
              {oldActivities?.map((activity) => (
                <React.Fragment key={activity.key}>
                  {activity.view}
                </React.Fragment>
              ))}
            </>
          ) : (
            <ActivitySeparator
              onClick={() => setIsForceShowingOldActivities(true)}
            >
              Show older than a week ago
            </ActivitySeparator>
          ))}
      </div>
      {hasMoreRecentActivitiesToFetch || hasMoreOldActivitiesToFetch ? (
        <InfiniteScrollWaypoint fetchMore={fetchMoreActivities} />
      ) : (
        <div className={styles.info}>
          Changes and updates within this post are listed here.
        </div>
      )}
    </>
  );
};

const getLinkToHistory = (
  post: DocumentType<typeof CONTROL_BAR_ACTIVITY_QUERY>['post'],
  historyGroup: HistoryGroup<
    DocumentType<typeof CONTROL_BAR_ACTIVITY_QUERY>['post']['histories'][number]
  >,
  canDisplayNewPostHistoryView: boolean,
) => {
  const path = historyPath(
    post,
    historyGroup.versionFrom,
    historyGroup.versionTo,
  );

  if (!isFeatureEnabled('history_v2')) {
    return path;
  }

  return historyGroup.isAccessible && canDisplayNewPostHistoryView
    ? path
    : undefined;
};

const getDescriptionAndIcon = (
  activity: DocumentType<
    typeof CONTROL_BAR_ACTIVITY_QUERY
  >['alphaPostActivities'][number],
) => {
  const result = (
    description: React.ReactNode,
    icon: string,
    iconColor?: ActivityItemIconColor,
  ) => ({ icon, description, iconColor });

  switch (activity.action) {
    case AlphaPostActivityType.Edit:
      return result('Edited this post', editIcon, 'pink');
    case AlphaPostActivityType.Create:
      return result('Created this post', draftsIcon);
    case AlphaPostActivityType.Verify:
      return result('Verified this post', verifiedIcon, 'green');
    case AlphaPostActivityType.Publish:
      return result('Published this post', publishIcon, 'pink');
    case AlphaPostActivityType.Unpublish:
      return result('Unpublished this post', unpublishIcon);
    case AlphaPostActivityType.Archive:
      return result('Archived this post', archiveIcon, 'pink');
    case AlphaPostActivityType.Unarchive:
      return result('Unarchived this post', unarchiveIcon);
    case AlphaPostActivityType.TransferOwner:
    case AlphaPostActivityType.VerifyExpired:
      return result(activity.action, helpIcon);
    default:
      exhaustiveMatchGuard(activity.action);
      throw new Error(`Unknown activity type: ${activity.action as string}`);
  }
};

export default ControlBarActivity;
